import React, { useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';

import { Input, BaseButton, Modal, ToolTip, useT } from '@repo/common';
import EmploymentTypeList from './EmploymentTypeList';
import LocationTypeList from './LocationTypeList';
import LocationInput from './LocationInput';
import KeyAchievementForm from './KeyAchievement/KeyAchievementForm';
import StepTitle from '../../Common/StepTitle';

import { ReactComponent as DragAndDropIcon } from '@repo/common/assets/icons/33px/icon_33px_drag-and-drop.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/24px/icon_24px_delete_outline.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-top.svg';
import { ReactComponent as InfoIcon } from '@repo/common/assets/icons/20px/icon_20px_info_outline.svg';
import { ReactComponent as TrophyIcon } from '@repo/common/assets/icons/20px/icon_20px_trophy-outline.svg';

const getInputState = (focused, clicked) => {
  if (clicked) {
    return 'active';
  }
  if (focused) {
    return 'focused';
  }
  return 'default';
};

const getIconState = (value, fieldState) => {
  if (value instanceof Date || value) return 'active';
  if (fieldState === 'active' || fieldState === 'focused') return 'focused';
  return 'default';
};

const ExperienceForm = ({ experience, setResumeData }) => {
  const t = useT();
  const [experiences, setExperiences] = useState([
    {
      id: uuidv4(),
      company: '',
      jobTitle: '',
      employmentType: '',
      employmentPeriod: '',
      location: '',
      locationType: '',
      headline: '',
      keyAchievements: [],
    },
  ]);

  const [openEmploymentTypeId, setOpenEmploymentTypeId] = useState(null);
  const [openLocationTypeId, setOpenLocationTypeId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [targetId, setTargetId] = useState(null);
  const [hoverDragAndDropId, setHoverDragAndDropId] = useState(null);
  const [hoverDeleteId, setHoverDeleteId] = useState(null);
  const [hoverInfoId, setHoverInfoId] = useState(null);
  const inputRefs = useRef({});
  const employmentDropdownRef = useRef(null);
  const locationTypeDropdownRef = useRef(null);

  const [focusedLocationId, setFocusedLocationId] = useState(null);

  const addExperience = () => {
    const newExp = {
      id: uuidv4(),
      company: '',
      jobTitle: '',
      employmentType: '',
      employmentPeriod: '',
      location: '',
      locationType: '',
      headline: '',
      keyAchievements: [],
    };
    setExperiences((prev) => [...prev, newExp]);

    setTimeout(() => {
      inputRefs.current[`company-${newExp.id}`]?.focus();
    }, 0);
  };

  useEffect(() => {
    if (experience && experience.length > 0) {
      setExperiences(
        experience.map((exp) => ({
          ...exp,
          id: exp.id || uuidv4(),
          keyAchievements: exp.keyAchievements || [],
        })),
      );
    }
  }, [experience]);

  const addKeyAchievement = (experienceId) => {
    setExperiences((prev) =>
      prev.map((exp) =>
        exp.id === experienceId
          ? {
              ...exp,
              keyAchievements: [
                ...exp.keyAchievements,
                {
                  id: uuidv4(),
                  title: '',
                  description: '',
                },
              ],
            }
          : exp,
      ),
    );
  };

  const openConfirmModal = (id) => {
    setTargetId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    const updated = experiences.filter((exp) => exp.id !== targetId);
    setExperiences(updated);
    setResumeData((prev) => ({
      ...prev,
      experience: updated,
    }));
    setShowDeleteModal(false);
    setTargetId(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setTargetId(null);
  };

  const handleInputChange = (id, field, value) => {
    const newExperiences = experiences.map((exp) =>
      exp.id === id ? { ...exp, [field]: value } : exp,
    );
    setExperiences(newExperiences);

    setResumeData((prev) => ({
      ...prev,
      experience: newExperiences,
    }));
  };

  const toggleEmploymentTypeList = (id, e) => {
    e.stopPropagation();
    setOpenEmploymentTypeId((prev) => (prev === id ? null : id));
  };

  const handleSelectEmploymentType = (id, employmentType) => {
    handleInputChange(id, 'employmentType', employmentType);
    setOpenEmploymentTypeId(null);
    inputRefs.current[id]?.blur();
  };

  const handleLocationSelect = (id, item) => {
    const newLocation = `${item.name}${
      item.country ? `, ${item.country}` : ''
    }`;

    handleInputChange(id, 'location', newLocation);
    setFocusedLocationId(null);
  };

  const handleManualLocationEntry = (id, currentQuery) => {
    handleInputChange(id, 'location', currentQuery);
    setFocusedLocationId(null);
  };

  const toggleLocationTypeList = (id, e) => {
    e.stopPropagation();
    setOpenLocationTypeId((prev) => (prev === id ? null : id));
  };

  const handleSelectLocationType = (id, locationType) => {
    handleInputChange(id, 'locationType', locationType);
    setOpenLocationTypeId(null);
    inputRefs.current[`locationType-${id}`]?.blur();
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    if (source.droppableId === 'experiences') {
      const items = Array.from(experiences);
      const [moved] = items.splice(source.index, 1);
      items.splice(destination.index, 0, moved);

      setExperiences(items);
      setResumeData((prev) => ({
        ...prev,
        experience: items,
      }));
    }

    if (source.droppableId.startsWith('key-achievements-')) {
      const expId = source.droppableId.replace('key-achievements-', '');

      const newExperiences = experiences.map((exp) => {
        if (exp.id !== expId) return exp;

        const items = Array.from(exp.keyAchievements);
        const [moved] = items.splice(source.index, 1);
        items.splice(destination.index, 0, moved);

        return { ...exp, keyAchievements: items };
      });

      setExperiences(newExperiences);
      setResumeData((prev) => ({
        ...prev,
        experience: newExperiences,
      }));
    }
  };

  const didFocus = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (didFocus.current) return;
    if (experiences.length === 0) return;

    const firstId = experiences[0].id;
    setTimeout(() => {
      inputRefs.current[`company-${firstId}`]?.focus();
      didFocus.current = true;
    }, 0);
  }, [experiences]);

  useEffect(() => {
    if (!openEmploymentTypeId && !openLocationTypeId) return;

    const handleClickOutside = (e) => {
      if (
        openEmploymentTypeId &&
        employmentDropdownRef.current &&
        !employmentDropdownRef.current.contains(e.target)
      ) {
        setOpenEmploymentTypeId(null);
      }

      if (
        openLocationTypeId &&
        locationTypeDropdownRef.current &&
        !locationTypeDropdownRef.current.contains(e.target)
      ) {
        setOpenLocationTypeId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openEmploymentTypeId, openLocationTypeId]);

  return (
    <div className='flex flex-col items-start w-full min-w-[460px] max-w-[950px] gap-6'>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId='experiences' direction='vertical'>
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className='flex flex-col gap-6 w-full'
            >
              {experiences.map((exp, index) => (
                <Draggable key={exp.id} draggableId={exp.id} index={index}>
                  {(provided, snapshot) => {
                    const style = {
                      ...provided.draggableProps.style,
                      transform: provided.draggableProps.style?.transform
                        ? provided.draggableProps.style.transform.replace(
                            /translate\(([-\d.]+)px,/,
                            'translate(0px,',
                          )
                        : undefined,
                    };

                    if (!snapshot.isDragging) {
                      delete style.transition;
                    }

                    return (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={style}
                        className={`flex w-full items-start`}
                      >
                        {/* Drag Handle */}
                        <div
                          {...provided.dragHandleProps}
                          className='relative flex-shrink-0 pt-2 cursor-grab active:cursor-grabbing text-grayscale-gray03 hover:text-icon-success active:text-icon-success'
                          onMouseEnter={() => setHoverDragAndDropId(exp.id)}
                          onMouseLeave={() => setHoverDragAndDropId(null)}
                        >
                          <DragAndDropIcon width={33} height={33} />
                          {hoverDragAndDropId === exp.id && (
                            <div className='absolute -top-12 left-1/2 -translate-x-1/2 z-50'>
                              <ToolTip
                                label={t(
                                  'Click and drag to move',
                                  '클릭한 채로 드래그하여 이동',
                                )}
                                position='bottom'
                                align='center'
                                state='accent'
                              />
                            </div>
                          )}
                        </div>

                        {/* Experience Form */}
                        <div className='flex-grow bg-grayscale-gray00 border border-grayscale-gray02 rounded-[8px] p-6'>
                          <div className='grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] w-full gap-6'>
                            {/* Company Or Prganization */}
                            <div className='flex flex-col gap-2 min-w-[320px]'>
                              <Input
                                label={t(
                                  'Company or organization',
                                  '회사 또는 단체',
                                )}
                                ref={(el) =>
                                  (inputRefs.current[`company-${exp.id}`] = el)
                                }
                                placeholder={t('e.g. Google', '예: Google')}
                                value={exp.company}
                                onChange={(e) =>
                                  handleInputChange(
                                    exp.id,
                                    'company',
                                    e.target.value,
                                  )
                                }
                                hideCount
                              />
                            </div>

                            {/* Job Title */}
                            <div className='flex flex-col gap-2 min-w-[320px]'>
                              <Input
                                label={t('Job title*', '직무명*')}
                                placeholder={t(
                                  'e.g. Visual Designer',
                                  '예: 비주얼 디자이너',
                                )}
                                value={exp.jobTitle}
                                onChange={(e) =>
                                  handleInputChange(
                                    exp.id,
                                    'jobTitle',
                                    e.target.value,
                                  )
                                }
                                hideCount
                              />
                            </div>

                            {/* Employment Type */}
                            <div className='flex flex-col gap-2 min-w-[320px]'>
                              <div
                                ref={employmentDropdownRef}
                                onClick={(e) =>
                                  toggleEmploymentTypeList(exp.id, e)
                                }
                                className='relative'
                              >
                                <Input
                                  ref={(el) => (inputRefs.current[exp.id] = el)}
                                  label={t('Employment type*', '고용 형태*')}
                                  placeholder={t(
                                    'e.g. Full-time',
                                    '예: 정규직',
                                  )}
                                  value={exp.employmentType}
                                  readOnly
                                  hideCount
                                  rightIcon={
                                    openEmploymentTypeId === exp.id ? (
                                      <ArrowTopIcon />
                                    ) : (
                                      <ArrowBottomIcon />
                                    )
                                  }
                                />
                                {openEmploymentTypeId === exp.id && (
                                  <div className='absolute top-[80px] left-0 z-20 w-full'>
                                    <EmploymentTypeList
                                      onSelect={(employmentType) =>
                                        handleSelectEmploymentType(
                                          exp.id,
                                          employmentType,
                                        )
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Employment Period */}
                            <div className='flex flex-col gap-2 min-w-[320px]'>
                              <Input
                                label={t('Employment period*', '근무 기간*')}
                                placeholder={t(
                                  'e.g. 2023.03 – 2025.04',
                                  '예: 2023.03 – 2025.04',
                                )}
                                value={exp.employmentPeriod}
                                onChange={(e) =>
                                  handleInputChange(
                                    exp.id,
                                    'employmentPeriod',
                                    e.target.value,
                                  )
                                }
                                hideCount
                              />
                            </div>

                            {/* Location */}
                            <div className='flex flex-col gap-2 min-w-[320px]'>
                              {(() => {
                                const isFocused = focusedLocationId === exp.id;
                                const locationState = getInputState(
                                  isFocused,
                                  false,
                                );
                                const locationIconState = getIconState(
                                  exp.location,
                                  locationState,
                                );

                                return (
                                  <LocationInput
                                    value={exp.location}
                                    focused={isFocused}
                                    state={locationState}
                                    iconState={locationIconState}
                                    onChange={(e) =>
                                      handleInputChange(
                                        exp.id,
                                        'location',
                                        e.target.value,
                                      )
                                    }
                                    onFocus={() => setFocusedLocationId(exp.id)}
                                    onBlur={() => setFocusedLocationId(null)}
                                    onSelect={(item) =>
                                      handleLocationSelect(exp.id, item)
                                    }
                                    onManualEnter={(value) =>
                                      handleManualLocationEntry(exp.id, value)
                                    }
                                    isResume
                                  />
                                );
                              })()}
                            </div>

                            {/* Location Type */}
                            <div className='flex flex-col gap-2 min-w-[320px]'>
                              <div
                                ref={locationTypeDropdownRef}
                                onClick={(e) =>
                                  toggleLocationTypeList(exp.id, e)
                                }
                                className='relative'
                              >
                                <Input
                                  ref={(el) =>
                                    (inputRefs.current[
                                      `locationType-${exp.id}`
                                    ] = el)
                                  }
                                  label={t('Location type', '근무 방식')}
                                  placeholder={t(
                                    'e.g. On-site',
                                    '예: 사무실 근무',
                                  )}
                                  value={exp.locationType}
                                  state={getInputState(
                                    openLocationTypeId === exp.id,
                                    false,
                                  )}
                                  readOnly
                                  hideCount
                                  rightIcon={
                                    openLocationTypeId === exp.id ? (
                                      <ArrowTopIcon />
                                    ) : (
                                      <ArrowBottomIcon />
                                    )
                                  }
                                />
                                {openLocationTypeId === exp.id && (
                                  <div className='absolute top-[80px] left-0 z-20 w-full'>
                                    <LocationTypeList
                                      onSelect={(locationType) =>
                                        handleSelectLocationType(
                                          exp.id,
                                          locationType,
                                        )
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Experience Headline */}
                          <div>
                            <div className='flex flex-col mt-6 gap-2 min-w-[320px]'>
                              <span className='text-grayscale-gray09 gap-[2px] flex flex-row'>
                                {t('Experience headline', '경력 헤드라인')}
                                <span className='relative'>
                                  <InfoIcon
                                    className='w-5 h-5'
                                    onMouseEnter={() => setHoverInfoId(exp.id)}
                                    onMouseLeave={() => setHoverInfoId(null)}
                                  />
                                  {hoverInfoId === exp.id && (
                                    <div className='absolute -top-2 left-[30px] z-50'>
                                      <ToolTip
                                        label={t(
                                          'Summarize the work in a few words',
                                          '업무 내용을 몇 단어로 요약해 주세요',
                                        )}
                                        position='left'
                                        align='top'
                                        state='accent'
                                      />
                                    </div>
                                  )}
                                </span>
                              </span>
                              <Input
                                label=''
                                placeholder={t(
                                  'e.g. Product Detail Page Redesign',
                                  '예: 상품 상세 페이지 리디자인',
                                )}
                                value={exp.headline}
                                onChange={(e) =>
                                  handleInputChange(
                                    exp.id,
                                    'headline',
                                    e.target.value,
                                  )
                                }
                                hideCount
                              />
                            </div>
                          </div>

                          {/* Add key Achievement */}
                          <div className='mt-6'>
                            {exp.keyAchievements.length === 0 && (
                              <BaseButton
                                type='secondary'
                                icon={<PlusIcon />}
                                width='190px'
                                onClick={() => addKeyAchievement(exp.id)}
                              >
                                {t('Add Key Achievement', '주요 성과 추가하기')}
                              </BaseButton>
                            )}

                            {exp.keyAchievements.length > 0 && (
                              <div className='mt-8'>
                                <StepTitle
                                  title={t('Key Achievement', '주요 성과')}
                                  icon={<TrophyIcon />}
                                />
                                <KeyAchievementForm
                                  experienceId={exp.id}
                                  achievements={exp.keyAchievements}
                                  onChange={(updatedAchievements) => {
                                    const newExperiences = experiences.map(
                                      (e) =>
                                        e.id === exp.id
                                          ? {
                                              ...e,
                                              keyAchievements:
                                                updatedAchievements,
                                            }
                                          : e,
                                    );

                                    setExperiences(newExperiences);
                                    setResumeData((prev) => ({
                                      ...prev,
                                      experience: newExperiences,
                                    }));
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Delete */}
                        <div className='flex-shrink-0 pt-1 relative'>
                          <button
                            className='w-10 h-10 flex justify-center items-center text-grayscale-gray04 hover:text-icon-success transition-colors'
                            onClick={() => openConfirmModal(exp.id)}
                            onMouseEnter={() => setHoverDeleteId(exp.id)}
                            onMouseLeave={() => setHoverDeleteId(null)}
                          >
                            <DeleteIcon width={24} height={24} />
                          </button>
                          {hoverDeleteId === exp.id && (
                            <div className='absolute -top-12 left-1/2 -translate-x-1/2 z-50'>
                              <ToolTip
                                label={t('Delete', '삭제')}
                                position='bottom'
                                align='center'
                                state='accent'
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <BaseButton
        type='secondary'
        size='l'
        icon={<PlusIcon />}
        width='100%'
        onClick={addExperience}
        textWrapperClass='flex-none'
      >
        {t('Add one more Experience', '경력 하나 더 추가하기')}
      </BaseButton>

      {showDeleteModal && (
        <Modal
          isOpen
          onClickLeftBtn={handleCancelDelete}
          onClickRightBtn={handleConfirmDelete}
          title={t(
            'Delete this experience entry?',
            '이 경력 항목을 삭제할까요?',
          )}
          subText={t(
            'This action can’t be undone.',
            '이 작업은 되돌릴 수 없습니다.',
          )}
          buttonSize='xl'
          leftButtonLabel={t('cancel', '취소')}
          leftHideIcon={true}
          rightButtonLabel={t('Delete', '삭제')}
          rightHideIcon={true}
          type='warning'
        />
      )}
    </div>
  );
};

export default ExperienceForm;
